import {useContext} from "react";
import {createIntl, createIntlCache, useIntl} from "react-intl";
import moment from "moment";
import {__RouterContext, useLocation as useReactRouterDomLocation} from "react-router-dom";
import {LANGUAGE} from "./i18n/locales/localeConstant";
import queryString from "query-string";
import {ENVS_MAP, YEAR_MONTH_DAY_FORMAT} from "./constant";
import {ADMIN_LOGIN, ARTICLES, IMPRINT, MARKET_PLACE} from "./routes";
import {emDash} from "./constant";
import {CATEGORIES, LocalizedContext} from "../store/providers/LocalizedProvider";

export const useI18N = () => {
    const {locale} = useIntl();
    const intl = useIntl();
    const i18n = (key, params) => key && intl.formatMessage({id: key}, params);
    const i18nLowerIfRequired = (translation) => {
        const translationString = i18n(translation);
        switch (locale) {
            case "en-gb":
                return translationString.toLowerCase();
            default:
                return translationString;
        }
    };

    return {
        i18n,
        i18nLowerIfRequired
    };
};

export const messageExists = (str) => {
    const intl = useIntl();
    return (intl.messages[str] === undefined) ? false : true;
};

export const useRouter = () => useContext(__RouterContext);

export const useParams = () => {
    const {match} = useRouter();
    return match.params;
};

export const useLocation = () => {
    const {location, history} = useRouter();
    const {search} = useReactRouterDomLocation();
    const [localizedState] = useContext(LocalizedContext);
    let {locale} = useIntl();

    function navigate(to, params, replace = false, scroll = true) {
        let navWithLocale;

        if (!to.startsWith("?")) {
            navWithLocale = `/${locale}` + to;
        } else {
            navWithLocale = to;
        }

        if (new URL(window.location.href).searchParams.get("displayIdleDialog")) {
            // User is logged out, keep param for idle dialog in the url
            const newUrl = new URL(window.location.origin + navWithLocale);
            newUrl.searchParams.set("displayIdleDialog", true);
            navWithLocale = newUrl.pathname + newUrl.search;
        }

        // Add / in the end of URL
        navWithLocale = addSlashInTheEndOfUrlIfNeeded(navWithLocale);

        if (replace) {
            history.replace(navWithLocale);
        } else {
            history.push(navWithLocale, params);
        }

        if (scroll) {
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, 300);
        }
    }

    const navigateToComposePage = (url) => {
        navigate(getComposePagePath(url));
    };

    const getComposePagePath = (url) => {
        const currentUrl = url.charAt(0) === "/" ? url : "/" + url;
        const pageId = localizedState?.slugsMap[LANGUAGE.EN][CATEGORIES.PAGES][currentUrl.replace("/", "")];
        if (localizedState?.idsMap.hasOwnProperty(pageId)) {
            return addSlashInTheEndOfUrlIfNeeded("/" + localizedState?.idsMap[pageId]["slug_" + locale.replace("-", "_")]);
        }
        return addSlashInTheEndOfUrlIfNeeded(currentUrl);
    };

    const isRouteInLocation = (route) => {
        return location?.pathname.replace("/" + locale + "/", "/").startsWith(route);
    };

    const isRouteTheExactLocation = (route) => {
        return location?.pathname.replace("/" + locale + "/", "/") === route;
    };

    /*
    * Gets the english slug of a compose page and returns the correct slug, based on user's locale.
    * */
    const getLocalizedPageSlug = (en_slug) => {
        try {
            let slug = en_slug;
            if (slug[0] === "/") {
                slug = slug.substring(1);
            }
            const pageId = localizedState?.slugsMap[LANGUAGE.EN][CATEGORIES.PAGES][slug];
            return localizedState?.idsMap[pageId]["slug_" + locale.replace("-", "_")];
        } catch (e) {
            return en_slug;
        }
    };

    return {
        location,
        search,
        navigate,
        isRouteInLocation,
        isRouteTheExactLocation,
        navigateToComposePage,
        getComposePagePath,
        getLocalizedPageSlug
    };
};

export const useDate = () => {
    const intl = useIntl();

    // use the application locale
    const formatDate = (value, opts) => {
        if (!value) {
            return "";
        }

        let options = opts ? {...opts} : {};

        if ((typeof value).toLowerCase() === "string" && !/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value)) {
            options.timeZone = "utc";
        }

        return intl.formatDate(value, options);
    };

    // use the specified locale
    const formatDateWithLocale = (locale, value, opts) => {
        // We dont use the useIntl hook, instead we create the intl, because in the hook case the intl object
        // takes the locale from the provider and shows all the dates with the current locale formatting
        if (!locale || !value) {
            return "";
        }
        const cache = createIntlCache();
        const intl = createIntl({
            locale: locale,
            messages: {}
        }, cache);
        let options = opts ? {...opts} : {};

        if ((typeof value).toLowerCase() === "string" && /\d{4}-\d{2}-\d{2}/.test(value)) {
            options.timeZone = "utc";
        }

        return intl.formatDate(value, options);
    };

    return {formatDate, formatDateWithLocale};
};

export const useCurrency = () => {
    const intl = useIntl();
    const twoZerosAfterDot = ".00";
    const twoZerosAfterComma = ",00";
    const dotEmDash = `.${emDash}`;

    // use the application locale
    const formatCurrency = (currency, value, noDashIfInteger = false, currencyDisplay = null) => {
        if (value % 1 === 0) {
            // No Decimals, must check cases
            const formated = intl.formatNumber(value, {style: "currency", currency: currency, currencyDisplay: currencyDisplay || "code"});

            if (formated.indexOf(".") > formated.indexOf(",")) {
                // Comma - Dot case
                // eg: GBP 1,500.00
                if (noDashIfInteger) {
                    return formated.replace(twoZerosAfterDot, "");
                } else {
                    return formated.replace(twoZerosAfterDot, dotEmDash);
                }
            } else {
                // Dot/NoDot - Comma case
                // eg: 1.500,00 EUR
                if (noDashIfInteger) {
                    return formated.replace(twoZerosAfterComma, "");
                } else {
                    return formated.replace(twoZerosAfterComma, dotEmDash);
                }
            }
        } else {
            // Has Decimals, just format and return
            return intl.formatNumber(value, {style: "currency", currency: currency, currencyDisplay: currencyDisplay || "code"});
        }
    };

    // use the specified locale
    const formatCurrencyWithLocale = (locale, currency, value) => {
        // We dont use the useIntl hook, instead we create the intl, because in the hook case the intl object
        // takes the locale from the provider and shows all the dates with the current locale formatting
        const cache = createIntlCache();
        const intl = createIntl({
            locale: locale,
            messages: {}
        }, cache);

        if (value % 1 === 0) {
            // No Decimals, must check cases
            const formated = intl.formatNumber(value, {style: "currency", currency: currency, currencyDisplay: "code"});

            if (formated.indexOf("." > formated.indexOf(","))) {
                // Comma - Dot case
                // eg: GBP 1,500.60
                return formated.replace(twoZerosAfterDot, dotEmDash);
            } else {
                // Dot/NoDot - Comma case
                // eg: 1.500,60 EUR, 1500,60 EUR
                return formated.replace(twoZerosAfterComma, dotEmDash);
            }

        } else {
            // Has Decimals, jsut format and return
            return intl.formatNumber(value, {style: "currency", currency: currency, currencyDisplay: "code"});
        }
    };

    return {formatCurrency, formatCurrencyWithLocale};
};

export const getQueryVariable = (variable) => {
    return queryString.parse(useLocation().location.search)[variable];
};

export const isMobile = () => {
    return !!(navigator.userAgent.match(/Android/i)
        || navigator.userAgent.match(/webOS/i)
        || navigator.userAgent.match(/iPhone/i)
        || navigator.userAgent.match(/iPad/i)
        || navigator.userAgent.match(/iPod/i)
        || navigator.userAgent.match(/BlackBerry/i)
        || navigator.userAgent.match(/Windows Phone/i));
};

export const isHighDensity = () => {
    return ((window.matchMedia && (window.matchMedia("only screen and (min-resolution: 124dpi), only screen and (min-resolution: 1.3dppx), only screen and (min-resolution: 48.8dpcm)").matches || window.matchMedia("only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 2.6/2), only screen and (min--moz-device-pixel-ratio: 1.3), only screen and (min-device-pixel-ratio: 1.3)").matches)) || (window.devicePixelRatio && window.devicePixelRatio > 1.3));
};

export const isRetina = () => {
    return ((window.matchMedia && (window.matchMedia("only screen and (min-resolution: 192dpi), only screen and (min-resolution: 2dppx), only screen and (min-resolution: 75.6dpcm)").matches || window.matchMedia("only screen and (-webkit-min-device-pixel-ratio: 2), only screen and (-o-min-device-pixel-ratio: 2/1), only screen and (min--moz-device-pixel-ratio: 2), only screen and (min-device-pixel-ratio: 2)").matches)) || (window.devicePixelRatio && window.devicePixelRatio >= 2)) && /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
};

export const getBrowser = () => {
    let browserName = "";
    if ((navigator.userAgent.indexOf("Opera")) !== -1) {
        browserName = "Opera";
    }
    else if ((navigator.userAgent.indexOf("MSIE")) !== -1) {
        browserName = "Microsoft Internet Explorer";
    }
    else if ((navigator.userAgent.indexOf("Chrome")) !== -1) {
        browserName = "Chrome";
    }
    else if ((navigator.userAgent.indexOf("Safari")) !== -1) {
        browserName = "Safari";
    }
    else if ((navigator.userAgent.indexOf("Firefox")) !== -1) {
        browserName = "Firefox";
    }
    return browserName;
};

// get distance between 2 elements
export const getDistance = (element1, element2) => {
    if (!(element1 && element2)) {
        return;
    }

    let elem1 = element1.getBoundingClientRect();
    let elem2 = element2.getBoundingClientRect();

    let el1x = elem1.left + elem1.width / 2;
    let el1y = elem1.top + elem1.height / 2;

    let el2x = elem2.left + elem1.width / 2;
    let el2y = elem2.top + elem1.height / 2;

    let distanceSquared = Math.pow(el1x - el2x, 2) + Math.pow(el1y - el2y, 2);
    let distance = Math.sqrt(distanceSquared);

    return distance;
};

export const getCurrency = (isoCode) => {
    let currency_symbols = {
        "USD": "$", // US Dollar
        "EUR": "€", // Euro
        "GBP": "£", // British Pound Sterling
        "ILS": "₪", // Israeli New Sheqel
        "PLN": "zł", // Polish Zloty
        "UAH": "₴", // Ukrainian Hryvnia
        "CHF": "CHF", // Swiss Franc
        "RUB": "₽", // Russian Ruble
        "SEK": "kr", // Sweden Krona
        "TRY": "₺", // Turkish Lira
        "CZK": "Kč", // Czeck Koruna
        "HUF": "Ft", // Hungarian Forint
        "MAD": "د.م.", // Moroccan Dirham
    };

    if (currency_symbols[isoCode] !== undefined) {
        return (currency_symbols[isoCode]);
    }
    return "";
};

/**
 * Order of language retrieval
 * 1. URL: If locale is provided in the url
 * 2. Local Storage: If user has already selected a language through menu this remains persistent
 * 3. Browser: If none of the above, language of browser is being retreived
 */

export const getLanguage = () => {

    // Get user selected language, this is persistent
    if (window.localStorage.getItem("locale")) {
        return window.localStorage.getItem("locale");
    }

    // Get navigator's language
    let lang = (navigator.languages && navigator.languages.length)
        ? navigator.languages[0]
        : navigator.userLanguage || navigator.language || navigator.browserLanguage || LANGUAGE.EN;

    if (lang.toLowerCase() === "de") {
        lang = LANGUAGE.DE;
    } else if (lang.toLowerCase() === "fr") {
        lang = LANGUAGE.FR;
    } else if (lang.toLowerCase() === "it") {
        lang = LANGUAGE.IT;
    } else if (lang.toLowerCase() === "nl") {
        lang = LANGUAGE.NL;
    } else if (lang.toLowerCase() === "en") {
        lang = LANGUAGE.EN;
    }

    return lang;
};

export const checkIfLocaleIsInUrl = (url) => {
    return (/\/[a-z]{2}-[a-z]{2}\//).test(url);
};

export const redirectLocale = (URL) => {
    let url = URL;
    let languages = [];
    for (let i in LANGUAGE) {
        languages.push("/" + LANGUAGE[i]);
    }

    languages.forEach(lan => {
        if (document.URL.includes(lan)) {
            url = lan + URL;
        }
    });

    return url;
};

export const isNoLoginRequired = (displayIdleDialog) => {
    const idleDialog = displayIdleDialog === "true" || (!location?.pathname?.includes(MARKET_PLACE) && location?.pathname?.endsWith(ADMIN_LOGIN));
    const legalPages = location?.pathname?.includes(IMPRINT);

    return idleDialog || legalPages;
};

export const isNotProduction = () => {
    return process.env.NODE_ENV !== ENVS_MAP.PRODUCTION;
};

export const cloneObject = (obj) => {
    return JSON.parse(JSON.stringify(obj));
};

/**
* reloads the page with the selected locale added in the url for every case (if the url has locale already or not)
*/
export const selectedLocaleChanged = (oldLocale, newLocale, localizedState, composePages) => {
    const localizeUrlIfNeeded = (localizedState, pathname, oldLocale, newLocale, urlContainsLocale = false) => {
        // Get url parts, removing empty strings from array
        const pathnameSplit = pathname.split("/").filter(part => part);
        let pathnameToReturn = pathname;
        // If the url contains the locale, remove it from the split list
        if (urlContainsLocale) {
            pathnameSplit.shift();
        }

        let countryId;
        let destinationId;
        let resortId;
        let articlePageId;
        switch (pathnameSplit.length) {
            case 1:
                countryId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.COUNTRIES, pathnameSplit[0]);
                if (countryId) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, countryId, pathnameSplit[0], newLocale, getLocalizedSlugFromId);
                }
                break;
            case 2:
                countryId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.COUNTRIES, pathnameSplit[0]);
                if (countryId) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, countryId, pathnameSplit[0], newLocale, getLocalizedSlugFromId);
                }

                destinationId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.DESTINATIONS, pathnameSplit[1]);
                if (destinationId) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, destinationId, pathnameSplit[1], newLocale, getLocalizedSlugFromId);
                }

                articlePageId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.PAGES, pathnameSplit[1]);
                if ("/" + pathnameSplit[0] === ARTICLES) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, articlePageId, pathnameSplit[1], newLocale, getLocalizedSlugFromId);
                }
                break;
            case 3:
                countryId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.COUNTRIES, pathnameSplit[0]);
                if (countryId) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, countryId, pathnameSplit[0], newLocale, getLocalizedSlugFromId);
                }
                destinationId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.DESTINATIONS, pathnameSplit[1]);
                if (destinationId) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, destinationId, pathnameSplit[1], newLocale, getLocalizedSlugFromId);
                }
                resortId = getIdFromSlug(localizedState, oldLocale, CATEGORIES.RESORTS, pathnameSplit[2]);
                if (resortId) {
                    pathnameToReturn = replaceInPathname(localizedState, pathnameToReturn, resortId, pathnameSplit[2], newLocale, getLocalizedSlugFromId);
                }
                break;
            default:
                break;
        }

        return pathnameToReturn;
    };

    const replaceInPathname = (localizedState, pathname, entryId, oldValue, newLocale, getLocalizedSlugFromId) => {
        return pathname.replace("/" + oldValue + "/", "/" + getLocalizedSlugFromId(localizedState, entryId, newLocale) + "/");
    };

    const getIdFromSlug = (localizedState, locale, category, slug) => {
        return localizedState?.slugsMap[locale][category][slug];
    };

    const getLocalizedSlugFromId = (localizedState, entryId, locale) => {
        return localizedState?.idsMap[entryId]["slug_" + locale.replace("-", "_")];
    };

    const determineIfComposePageAndGetPathname = (composePages, oldLocale, newLocale) => {
        let newPathname = undefined;
        let newSlug;

        if (!composePages) {
            return newPathname;
        }

        let slug = window.location.pathname.replace(/\/[a-z]{2}-[a-z]{2}\//, "");
        // Remove trailing slashes
        slug = (slug.endsWith("/") ? slug.slice(0, -1) : slug);

        if (!slug) {
            return newPathname;
        }

        // find compose page
        const page = composePages?.find(cp =>
            (cp[`slug_${oldLocale.replace("-", "_")}`] === slug) ||
            (cp[`slug_${oldLocale.replace("-", "_")}`] === `/${slug}`) ||
            (cp[`slug_${oldLocale.replace("-", "_")}`] === `/${slug}/`)
        );

        if (!page) {
            return newPathname;
        }

        newSlug = page[`slug_${newLocale.replace("-", "_")}`];

        if (!newSlug) {
            return newPathname;
        }

        if (newSlug.startsWith("/")) {
            newSlug = newSlug.substring(1);
        }

        newPathname = `/${newLocale}/${newSlug}`;
        return newPathname;
    };

    // Check if compose page first
    const composePagePathname = determineIfComposePageAndGetPathname(composePages, oldLocale, newLocale);
    if (composePagePathname) {
        return window.location.assign(composePagePathname);
    }

    // Handle static pages
    const pathname = checkIfLocaleIsInUrl(window.location.pathname) ? (window.location.pathname.replace(/[a-z]{2}-[a-z]{2}/, newLocale)) : ("/" + newLocale + window.location.pathname);
    window.location.assign(window.location.origin + localizeUrlIfNeeded(localizedState, pathname, oldLocale, newLocale, true) + window.location.search);
};

/**
 * Reloads the window if the previous url was outside from the app, generally the case that the user just visit the site and their browser settings is not "en-gb"
*/
export const reloadWithLocale = (locale) => {
    if (!checkIfLocaleIsInUrl(window.location.pathname) && (!document.referrer.includes(window.location.origin))) {
        window.location.assign(window.location.origin + "/" + locale + window.location.pathname + window.location.search);
    }
};

export const snakeToCamelCase = (obj) => {
    const isObject = (val) => val && typeof val === "object" && !Array.isArray(val);
    const isArray = (val) => Array.isArray(val);

    const transform = (key) => key.replace(/(_\w)/g, k => k[1].toUpperCase());

    const convert = (item) => {
        if (isArray(item)) {
            return item.map(convert); // Recursively apply conversion to each element in the array
        } else if (isObject(item)) {
            let newObj = {};
            for (let key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    newObj[transform(key)] = convert(item[key]); // Recursively convert object properties
                }
            }
            return newObj;
        }
        return item; // Return the value if it's not an array or object
    };

    return convert(obj); // Start the conversion with the input object
};

export const camelToSnakeCase = (obj) => {
    let rtn = {};
    const transform = (key) => {
        return key.replace(/([A-Z])/g, "_$1").toLowerCase();
    };

    for (let key in obj) {
        rtn[transform(key)] = obj[key];
    }

    return rtn;
};

export const hasVerticalScroll = (node) => {
    if (!node) {
        if (window.innerHeight) {
            return document.body.offsetHeight > window.innerHeight;
        }
        return (document.documentElement.scrollHeight > document.documentElement.offsetHeight)
            || (document.body.scrollHeight > document.body.offsetHeight);
    }
    return node.scrollHeight > node.offsetHeight;
};

export const useLocalizedUrl = () => {
    const {locale} = useIntl();

    const getLocalizedUrl = (to) => {
        if (to) {
            if (!to.startsWith("?") && !to.includes(`/${locale}/`)) {
                return addSlashInTheEndOfUrlIfNeeded(`/${locale}` + to);
            } else {
                return addSlashInTheEndOfUrlIfNeeded(to);
            }
        }
    };

    return {
        getLocalizedUrl
    };
};

/*
* Appends a back-slash at the end of the url, if it is missing.
* */
export const addSlashInTheEndOfUrlIfNeeded = (url) => {
    if (url?.charAt(0) === "?") {
        return url;
    }
    return url?.includes("?") ? url?.replace("/?", "?").replace("?", "/?") : (url?.slice(-1) === "/" ? url : (url + "/"));
};

export const isAdult = (birthDate) => {
    let res;
    if (birthDate) {
        const age = moment().diff(moment(birthDate), "years");
        res = age >= 18;
    }
    return res;
};

// TODO: maybe this should be moved to ./date.js
export const howManyDaysFromNow = (date_from) => {
    let res = 0;
    const dateFrom = moment(date_from, YEAR_MONTH_DAY_FORMAT).startOf("day");
    const dateCurrent = moment().startOf("day");
    if(dateFrom.isValid() && dateCurrent.isValid()) {
        res = dateFrom.diff(dateCurrent, "days");
    }
    return res;
};

export const isEmpty = (value) => {
    return (value === undefined || value === null || (typeof value === "string" && value.trim().length === 0));
};

export const uuidv4 = () => {
    if(crypto) {
        if (crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
    } else {
        let d = new Date().getTime();
        let d2 = ((typeof performance !== "undefined") && performance.now && (performance.now() * 1000)) || 0;
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
            let r = Math.random() * 16;
            if (d > 0) {
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else {
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }
            return (c === "x" ? r : (r & 0x7 | 0x8)).toString(16);
        });
    }
};

export const checkArraysEqual = (arr1, arr2) => {
    // If arrays have different lengths, they cannot be equal
    if (arr1.length !== arr2.length) {
        return false;
    }

    // Create a frequency counter for the first array
    let frequencyCounter1 = {};
    for (let num of arr1) {
        frequencyCounter1[num] = (frequencyCounter1[num] || 0) + 1;
    }

    // Create a frequency counter for the second array
    let frequencyCounter2 = {};
    for (let num of arr2) {
        frequencyCounter2[num] = (frequencyCounter2[num] || 0) + 1;
    }

    // Compare the frequency counters
    for (let key in frequencyCounter1) {
        if (frequencyCounter1[key] !== frequencyCounter2[key]) {
            return false;
        }
    }

    return true;
};

export const removeTrailingSlash = (str) => {
    return str.endsWith("/") ? str.slice(0, -1) : str;
};

export const isNumber = (value) => {
    return value !== null && value !== undefined && !isNaN(value);
};

export const getResortDetailsPageURL = (resort) => {
    return `/${resort?.destination?.country?.slug}/${resort?.destination?.slug}/${resort?.slug}`;
};
